import React from "react";
import { Box, Typography, Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Star, Zap, Crown, Gem, Gift, Sparkles } from "lucide-react";
import CoinIcon from "../../assets/svgs/CoinIcon";

const useStyles = makeStyles(theme => ({
  card: {
    background:
      "linear-gradient(to bottom, #AC881E 0%, #D0A946 33%, #F6EF89 66%,#F2CB69 100%)",
    borderRadius: "16px",
    padding: 1,
    position: "relative",
    transition: "all 0.3s ease",
    cursor: "pointer",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
      borderColor: "#FE5A97",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  indentBox: {
    borderRadius: "16px",
    padding: theme.spacing(3),
    backgroundColor: "#0C0A09",
  },
  popularCard: {},
  popularBadge: {
    position: "absolute",
    top: "-12px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#FE5A97",
    color: "white",
    padding: "6px 16px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    boxShadow: "0 4px 12px rgba(254, 90, 151, 0.3)",
  },
  packageIcon: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
    "& svg": {
      width: "48px",
      height: "48px",
      color: "#FE5A97",
    },
  },
  packageName: {
    color: "#FFFFFFCC",
    fontSize: "20px",
    fontWeight: 400,
    textAlign: "left",
    textTransform: "capitalize",
    marginBottom: theme.spacing(1),
  },
  packageDescription: {
    color: "#ccc",
    fontSize: "14px",
    textAlign: "center",
    marginBottom: theme.spacing(2),
    lineHeight: 1.4,
  },
  creditsInfo: {
    gap: 12,
    display: "flex",
    alignItems: "center",
  },
  creditsAmount: {
    color: "#FFF",
    fontSize: 28,
    fontWeight: 800,
    lineHeight: 1,
    [theme.breakpoints.down("sm")]: {
      fontSize: 20,
    },
  },
  creditsLabel: {
    color: "#999",
    fontSize: "14px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  priceInfo: {
    textAlign: "center",
    marginBottom: theme.spacing(2),
  },
  price: {
    color: "white",
    fontSize: "28px",
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
    [theme.breakpoints.down("sm")]: {
      fontSize: "24px",
    },
  },
  costPerCredit: {
    color: "#999",
    fontSize: "12px",
  },
  discountBadge: {
    backgroundColor: "#28a745",
    color: "white",
    padding: "4px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: 600,
    marginLeft: theme.spacing(1),
  },
  targetSegment: {
    backgroundColor: "#333",
    color: "#ccc",
    padding: "8px 12px",
    borderRadius: "8px",
    fontSize: "12px",
    textAlign: "center",
    marginBottom: theme.spacing(2),
    fontStyle: "italic",
  },
  purchaseButton: {
    width: "100%",
    backgroundColor: "#F1A21F",
    color: "white",
    padding: "12px 24px",
    borderRadius: "8px",
    textTransform: "none",
    fontSize: "16px",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#E9970F",
    },
    "&:disabled": {
      backgroundColor: "#666",
      color: "#999",
    },
  },
  packagePrice: {
    color: "white",
    fontSize: 38,
    fontWeight: 600,
    marginBottom: 5,
  },
  popularButton: {
    backgroundColor: "#F1A21F",
    "&:hover": {
      backgroundColor: "#E9970F",
    },
  },
  valueInfo: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: "1px solid #333",
  },
  valueItem: {
    textAlign: "center",
    flex: 1,
  },
  valueLabel: {
    color: "#999",
    fontSize: "10px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: theme.spacing(0.5),
  },
  valueAmount: {
    color: "white",
    fontSize: "12px",
    fontWeight: 600,
  },
}));

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  costPerCredit: number;
  description: string;
  targetSegment: string;
  popular: boolean;
  sortOrder: number;
  currency: string;
  totalValue: number;
  effectiveCostPerCredit: number;
  discountPercentage: number;
}

interface CreditPackageCardProps {
  package: CreditPackage;
  onPurchase: (packageId: string) => void;
  loading?: boolean;
}

const getPackageIcon = (packageId: string) => {
  switch (packageId) {
    case "flirt_pack":
      return <Gift size={48} />;
    case "passion_pack":
      return <Star size={48} />;
    case "obsession_pack":
      return <Zap size={48} />;
    case "vip_lover_pack":
      return <Crown size={48} />;
    case "luxury_pack":
      return <Gem size={48} />;
    case "elite_pack":
      return <Sparkles size={48} />;
    default:
      return <Gift size={48} />;
  }
};

const getDiscountPercentage = (
  packageName: string,
  credits: number
): number | null => {
  const nameLower = packageName.toLowerCase();

  // Для 20 credits - без знижки
  if (credits === 20) {
    return null;
  }

  // Перевірка за назвою пакету
  if (nameLower.includes("passion")) {
    return 20;
  }
  if (nameLower.includes("obsession")) {
    return 34;
  }
  if (nameLower.includes("vip lover") || nameLower.includes("vip_lover")) {
    return 46;
  }
  if (credits === 1000) {
    return 60;
  }
  // Для останнього пакету (найбільший)
  if (credits > 1000) {
    return 66;
  }

  return null;
};

const CreditPackageCard: React.FC<CreditPackageCardProps> = ({
  package: packageData,
  onPurchase,
  loading = false,
}) => {
  const classes = useStyles();

  const handlePurchase = () => {
    onPurchase(packageData.id);
  };

  return (
    <Box
      className={`${classes.card} ${
        packageData.popular ? classes.popularCard : ""
      }`}
    >
      <Box className={classes.indentBox}>
        <Box className={classes.creditsInfo}>
          <CoinIcon />
          <Typography className={classes.creditsAmount}>
            <b>{packageData.credits}</b> Credits
          </Typography>
        </Box>

        <Typography className={classes.packageName}>
          {packageData.name}
        </Typography>

        <Box
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
          }}
        >
          {/* Price */}
          <Typography className={classes.packagePrice}>
            ${packageData.price}
          </Typography>

          {/* Price per credit and discount in one row */}
          <Box
            style={{
              display: "flex",
              flexDirection: "column-reverse",
            }}
          >
            <Typography
              style={{
                fontSize: "12px",
                color: "#747474",
              }}
            >
              $0.50/Credit
            </Typography>
            {(() => {
              // Використовуємо discountPercentage з даних, якщо є, інакше визначаємо з назви/кредитів
              const discount =
                packageData.discountPercentage !== undefined &&
                packageData.discountPercentage > 0
                  ? packageData.discountPercentage
                  : getDiscountPercentage(
                      packageData.name,
                      packageData.credits
                    );
              return discount !== null && discount > 0 ? (
                <Typography
                  style={{
                    fontSize: "20px",
                    color: "#E9BD4F",
                    fontWeight: 600,
                  }}
                >
                  {discount}% OFF
                </Typography>
              ) : null;
            })()}
          </Box>
        </Box>

        <Button
          className={`${classes.purchaseButton} ${
            packageData.popular ? classes.popularButton : ""
          }`}
          onClick={handlePurchase}
          disabled={loading}
        >
          {loading ? "Processing..." : `Buy Credits`}
        </Button>
      </Box>
    </Box>
  );
};

export default CreditPackageCard;
